import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { T } from "../../app/tokens.js";
import { mediaRequest as apiFetch } from "../../data/projectMediaClient.js";
import { buildOfficeDrawerUrl } from "../../services/buildUrl.js";
import { buildMarketRecordUrl } from "../places/marketRecord.js";
import { onLocationMediaChanged } from "../../data/locationMediaClient.js";
import { onMarketMediaChanged } from "../../data/marketMediaClient.js";

export function photoCoverageRows(rows, kind, missing, query) {
  const q = query.trim().toLocaleLowerCase();
  return rows.filter(row => (!missing || !(kind === "office" ? row.imageUrl : row.photoUrl))
    && (!q || [row.id, row.name, row.locationAlias, row.market].filter(Boolean).join(" ").toLocaleLowerCase().includes(q)));
}
export function PhotoCoverage({ kind }) {
  const [rows, setRows] = useState([]);
  const [missing, setMissing] = useState(false);
  const [query, setQuery] = useState("");
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  useEffect(() => {
    let active = true;
    const load = () => apiFetch(kind === "office" ? "/api/locations" : "/api/markets?view=all")
      .then(data => {
        if (!Array.isArray(data)) throw new Error("Invalid photo coverage response.");
        if (active) { setRows(data); setError(null); setLoading(false); }
      }).catch(err => { if (active) { setError(err.message); setLoading(false); } });
    setLoading(true);
    load();
    const refresh = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", refresh);
    const interval = setInterval(refresh, 60000);
    return () => { active = false; clearInterval(interval); window.removeEventListener("focus", refresh); };
  }, [kind, retry]);
  // A drawer mutation returns to this mounted coverage page without a reload.
  useEffect(() => {
    const subscribe = kind === "office" ? onLocationMediaChanged : onMarketMediaChanged;
    const offs = rows.map(row => subscribe(row.id, () => setRetry(n => n + 1)));
    return () => offs.forEach(off => off());
  }, [rows, kind]);
  const filtered = photoCoverageRows(rows, kind, missing, query);
  return <section aria-label={`${kind === "office" ? "Office" : "Market"} photo coverage`} data-testid={`photo-coverage-${kind}`}>
    <h2 style={{ color: T.dark, fontSize: T.fsLg }}>Photo coverage</h2>
    <p style={{ color: T.mid, fontSize: T.fsSm }}>Photos are managed on each record’s Media tab. Select a record to open Media.</p>
    <div style={{ display: "flex", gap: 16, marginBottom: 16, alignItems: "center" }}>
      <input aria-label="Search photo coverage" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search records" />
      <label><input type="checkbox" checked={missing} onChange={e => setMissing(e.target.checked)} /> Missing photo</label>
      <span style={{ color: T.mid, fontSize: T.fsSm }}>{filtered.length} of {rows.length} records</span>
    </div>
    {loading && <p role="status">Loading photo coverage…</p>}
    {error && <p role="alert">{error} <button onClick={() => setRetry(n => n + 1)}>Retry</button></p>}
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(210px,1fr))", gap: 16 }}>
      {filtered.map(row => <CoverageCard key={row.id} row={row} kind={kind} returnPath={`${location.pathname}${location.search}`} />)}
    </div>
    {!loading && !error && filtered.length === 0 && <p>No records match these filters.</p>}
  </section>;
}
function CoverageCard({ row, kind, returnPath }) {
  const src = kind === "office" ? row.imageUrl : row.photoUrl;
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  return <Link to={kind === "office" ? buildOfficeDrawerUrl(row.id, "media") : buildMarketRecordUrl(row.id, "media")}
    state={{ returnPath }} aria-label={`Open ${row.name || row.locationAlias || row.id} Media`}
    style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", textDecoration: "none", color: T.dark, background: T.card }}>
    <div style={{ height: 130, background: T.lightGray, display: "grid", placeItems: "center", color: T.mid }}>
      {src && !broken ? <img src={src} alt="" onError={() => setBroken(true)} style={{ width: "100%", height: 130, objectFit: "cover" }} />
        : <span>{broken ? "Photo unavailable" : "No photo"}</span>}
    </div>
    <div style={{ padding: 12 }}><strong>{row.name || row.locationAlias || row.id}</strong><div style={{ fontSize: T.fsXs, color: T.mid }}>{row.id} · {row.region}</div><div style={{ color: T.blue, fontSize: T.fsSm, marginTop: 8 }}>Open Media</div></div>
  </Link>;
}
